import { Controller } from "@hotwired/stimulus";
import { loadStripe } from "@stripe/stripe-js";

export default class extends Controller {
  static targets = ["paymentElement", "error", "spinner"]

  connect() {
    // Listen for a custom event signaling that a discount has been applied.
    this.element.addEventListener("discount:applied", this.updatePaymentIntent.bind(this));
    this.element.addEventListener("discount:removed", this.updatePaymentIntent.bind(this));

    this.fetchClientSecret().then(result => {
      if (result && result.clientSecret && result.stripeAccountId) {
        this.initializeStripe(result.clientSecret, result.stripeAccountId);
      } else {
        console.error("Failed to fetch client secret or stripe account id");
      }
    }).catch(error => {
      console.error("An error occurred:", error);
    });
  }

  async fetchClientSecret() {
    try {
      const response = await fetch('/setup_checkout');
      if (!response.ok) throw new Error('Network response was not ok');
      const data = await response.json();
      return {
        clientSecret: data.clientSecret,
        stripeAccountId: data.stripeAccountId,
        paymentIntentId: data.paymentIntentId
      };
    } catch (error) {
      console.error("Error fetching client secret:", error);
      return null;
    }
  }

  async initializeStripe(clientSecret, stripeAccountId) {
    // process.env.STRIPE_PUBLISHABLE
    const stripe = await loadStripe(process.env.STRIPE_PUBLISHABLE, { stripeAccount: stripeAccountId });
    const options = {
      clientSecret: clientSecret,
      layout: { type: 'tabs', defaultCollapsed: false }
    };
    const elements = stripe.elements(options);
    const paymentElement = elements.create("payment");

    // Mount the Payment Element using the Stimulus target.
    if (this.hasPaymentElementTarget) {
      paymentElement.mount(this.paymentElementTarget);
    }

    this.stripe = stripe;
    this.elements = elements;
    this.clientSecret = clientSecret;
  }

  async updatePaymentIntent() {
    try {
      // Show spinner during update.
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.classList.remove("hidden");
      }
      const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute("content");
      const response = await fetch('/update_checkout', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken
            }
          });
      if (!response.ok) throw new Error("Failed to update PaymentIntent");
      const data = await response.json();

      // Update the client secret.
      this.clientSecret = data.clientSecret;

      // Clear and reinitialize the Payment Element using the target.
      if (this.hasPaymentElementTarget) {
        this.paymentElementTarget.innerHTML = "";
        const options = {
          clientSecret: this.clientSecret,
          layout: { type: 'tabs', defaultCollapsed: false }
        };
        this.elements = this.stripe.elements(options);
        const paymentElement = this.elements.create("payment");
        paymentElement.mount(this.paymentElementTarget);
      }
      console.log("PaymentIntent updated successfully.");
    } catch (error) {
      console.error("Error updating PaymentIntent:", error);
      if (this.hasErrorTarget) {
        this.errorTarget.textContent = error.message;
      }
    } finally {
      // Hide spinner after update.
      if (this.hasSpinnerTarget) {
        this.spinnerTarget.classList.add("hidden");
      }
    }
  }

  async submitPayment(event) {
    event.preventDefault();
    const button = this.element.querySelector('.pay-now-btn');
    button.disabled = true;
    try {
      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: {
          return_url: `${window.location.origin}/order_success`,
        },
      });
      if (error) {
        console.error('Payment error:', error.message);
        if (this.hasErrorTarget) this.errorTarget.textContent = error.message;
      } else {
        console.log("The payment has been processed.");
      }
    } catch (error) {
      console.error('Payment exception:', error);
    } finally {
      button.disabled = false;
    }
  }
}
